import { useMemo, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { adminKeys } from "@/lib/query";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate, formatInteger, formatMoney } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  useAdminTable,
} from "@/components/admin/AdminTableControls";
import {
  AdminDataTable,
  AdminRowActions,
  type AdminDataTableColumn,
} from "@/components/admin/AdminDataTable";
import AdminFormHeader from "@/components/admin/AdminFormHeader";
import ConfirmModal from "@/components/ui/ConfirmModal";

type PriceTier = {
  name: string;
  tierType: "deposit" | "full" | "addon";
  priceCents: number;
  balanceDueCents?: number;
  description?: string;
  stripePriceId?: string;
};

type Retreat = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  status: "draft" | "open" | "sold-out" | "past";
  klaviyoListId: string | null;
  capacity: number | null;
  startDate: number | string;
  priceTiers: PriceTier[];
  registrations: number;
  activeRegistrations: number;
  balanceDueRegistrations: number;
  fullyPaidRegistrations: number;
  cancelledRegistrations: number;
  totalPaidCents: number;
  balanceDueCents: number;
};

type RetreatRegistration = {
  id: string;
  name: string | null;
  email: string;
  tierName: string | null;
  amountPaidCents: number;
  balanceDueCents: number;
  status: string;
  createdAt: string;
};

type RegSummary = {
  count: number;
  totalPaidCents: number;
  balanceDueCents: number;
  outstandingCount: number;
};

type FollowupResult = {
  eligible: number;
  generated: number;
  emailed: number;
  notEmailed: number;
  skipped: number;
  failed: number;
  failures: Array<{ email: string; error: string }>;
};

type RetreatSortKey = "title" | "startDate" | "status" | "seats" | "paid" | "balance" | "registrations";

type TierForm = {
  name: string;
  tierType: "deposit" | "full" | "addon";
  priceDollars: string;
  balanceDueDollars: string;
  description: string;
};

type FormState = {
  title: string;
  subtitle: string;
  status: "draft" | "open" | "sold-out" | "past";
  capacity: string;
  klaviyoListId: string;
  tiers: TierForm[];
};

function tierToForm(t: PriceTier): TierForm {
  return {
    name: t.name,
    tierType: t.tierType,
    priceDollars: (t.priceCents / 100).toFixed(2),
    balanceDueDollars: t.balanceDueCents ? (t.balanceDueCents / 100).toFixed(2) : "",
    description: t.description ?? "",
  };
}

function toForm(r: Retreat): FormState {
  return {
    title: r.title,
    subtitle: r.subtitle ?? "",
    status: r.status,
    capacity: r.capacity?.toString() ?? "",
    klaviyoListId: r.klaviyoListId ?? "",
    tiers: r.priceTiers.map(tierToForm),
  };
}

const emptyTier: TierForm = {
  name: "",
  tierType: "full",
  priceDollars: "",
  balanceDueDollars: "",
  description: "",
};

function retreatSearchText(r: Retreat) {
  return [
    r.title,
    r.subtitle,
    r.slug,
    r.status,
    r.klaviyoListId,
    formatDate(r.startDate),
    formatMoney(r.totalPaidCents),
    formatMoney(r.balanceDueCents),
  ]
    .filter(Boolean)
    .join(" ");
}

const retreatSorters: Record<RetreatSortKey, (row: Retreat) => string | number> = {
  title: (row) => row.title,
  startDate: (row) => dateSortValue(row.startDate),
  status: (row) => row.status,
  seats: (row) => row.activeRegistrations,
  paid: (row) => row.totalPaidCents,
  balance: (row) => row.balanceDueCents,
  registrations: (row) => row.registrations,
};

export default function AdminRetreats() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Retreat | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);

  // per-retreat registrations (purchases) — expandable row
  const [openRegs, setOpenRegs] = useState<string | null>(null);
  const [regs, setRegs] = useState<RetreatRegistration[]>([]);
  const [regsSummary, setRegsSummary] = useState<RegSummary | null>(null);
  const [regsLoading, setRegsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [followupRetreat, setFollowupRetreat] = useState<Retreat | null>(null);
  const [followupBusy, setFollowupBusy] = useState(false);
  const [followupMsg, setFollowupMsg] = useState<Record<string, { text: string; ok: boolean }>>({});

  const retreatsQuery = useQuery({
    queryKey: adminKeys.retreats(),
    queryFn: () => api.get<{ retreats: Retreat[] }>("/api/admin/retreats"),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const list = retreatsQuery.data?.retreats ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: adminKeys.retreats() });

  async function loadRegistrations(retreatId: string) {
    setRegsLoading(true);
    setRegs([]);
    setRegsSummary(null);
    try {
      const r = await api.get<{ registrations: RetreatRegistration[]; summary: RegSummary }>(
        `/api/admin/retreats/${retreatId}/registrations`,
      );
      setRegs(r.registrations);
      setRegsSummary(r.summary);
    } finally {
      setRegsLoading(false);
    }
  }

  async function viewRegistrations(retreatId: string) {
    if (openRegs === retreatId) {
      setOpenRegs(null);
      return;
    }
    setOpenRegs(retreatId);
    await loadRegistrations(retreatId);
  }

  function startEdit(retreat: Retreat) {
    setForm(toForm(retreat));
    setEditing(retreat);
    setErr(null);
    setSyncMsg(null);
  }

  function cancel() {
    setEditing(null);
    setForm(null);
    setErr(null);
    setSyncMsg(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!editing || !form) return;
    setBusy(true);
    setErr(null);
    const payload = {
      title: form.title,
      subtitle: form.subtitle || null,
      status: form.status,
      capacity: form.capacity ? parseInt(form.capacity, 10) : null,
      klaviyoListId: form.klaviyoListId || null,
      priceTiers: form.tiers.map((t) => ({
        name: t.name,
        tierType: t.tierType,
        priceCents: Math.round(parseFloat(t.priceDollars || "0") * 100),
        balanceDueCents: t.balanceDueDollars
          ? Math.round(parseFloat(t.balanceDueDollars) * 100)
          : undefined,
        description: t.description || undefined,
      })),
    };
    try {
      await api.put(`/api/admin/retreats/${editing.id}`, payload);
      await refresh();
      cancel();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function syncKlaviyo() {
    if (!editing) return;
    setSyncBusy(true);
    setSyncMsg(null);
    try {
      const r = await api.post<{ ok: boolean; synced: number; failed: number; skipped: number }>(
        `/api/admin/retreats/${editing.id}/sync-klaviyo`,
      );
      setSyncMsg(`Done — ${r.synced} subscribed, ${r.failed} failed, ${r.skipped} cancelled skipped.`);
    } catch (e2) {
      setSyncMsg(e2 instanceof Error ? e2.message : "Sync failed");
    } finally {
      setSyncBusy(false);
    }
  }

  async function sendBalanceFollowups() {
    if (!followupRetreat) return;
    setFollowupBusy(true);
    setFollowupMsg((m) => ({
      ...m,
      [followupRetreat.id]: { text: "Sending followups...", ok: true },
    }));
    try {
      const result = await api.post<FollowupResult>(
        `/api/admin/retreats/${followupRetreat.id}/balance-followups`,
      );
      const failed = result.failed > 0 ? `, ${result.failed} failed` : "";
      const notEmailed = result.notEmailed > 0 ? `, ${result.notEmailed} links not emailed` : "";
      setFollowupMsg((m) => ({
        ...m,
        [followupRetreat.id]: {
          text: `${result.emailed} emailed${notEmailed}${failed}`,
          ok: result.failed === 0,
        },
      }));
      await refresh();
      if (openRegs === followupRetreat.id) await loadRegistrations(followupRetreat.id);
      setFollowupRetreat(null);
    } catch (e2) {
      setFollowupMsg((m) => ({
        ...m,
        [followupRetreat.id]: {
          text: e2 instanceof Error ? e2.message : "Followup failed",
          ok: false,
        },
      }));
    } finally {
      setFollowupBusy(false);
    }
  }

  function setTier(i: number, patch: Partial<TierForm>) {
    if (!form) return;
    const tiers = form.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    setForm({ ...form, tiers });
  }

  function addTier() {
    if (!form) return;
    setForm({ ...form, tiers: [...form.tiers, { ...emptyTier }] });
  }

  function removeTier(i: number) {
    if (!form) return;
    setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });
  }

  const filters = useMemo(
    () => [
      (r: Retreat) => statusFilter === "all" || r.status === statusFilter,
      (r: Retreat) =>
        balanceFilter === "all" ||
        (balanceFilter === "outstanding" && r.balanceDueRegistrations > 0) ||
        (balanceFilter === "paid" && r.activeRegistrations > 0 && r.balanceDueRegistrations === 0) ||
        (balanceFilter === "empty" && r.registrations === 0),
    ],
    [balanceFilter, statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Retreat, RetreatSortKey>({
    rows: list,
    search,
    initialSort: { key: "startDate", direction: "asc" },
    sorters: retreatSorters,
    searchText: retreatSearchText,
    filters,
  });

  const columns: Array<AdminDataTableColumn<Retreat, RetreatSortKey>> = [
    {
      id: "title",
      header: "Title",
      sortKey: "title",
      width: "32rem",
      className: "title-cell",
      render: (r) => (
        <>
          <strong>{r.title}</strong>
          <span className="slug">{r.slug}</span>
          <span className="slug">Klaviyo: {r.klaviyoListId ?? "-"}</span>
        </>
      ),
    },
    {
      id: "date",
      header: "Date",
      sortKey: "startDate",
      width: "8.5rem",
      nowrap: true,
      render: (r) => formatDate(r.startDate),
    },
    { id: "status", header: "Status", sortKey: "status", width: "7rem", render: (r) => r.status },
    {
      id: "seats",
      header: "Seats",
      sortKey: "seats",
      width: "8.5rem",
      nowrap: true,
      render: (r) => {
        const seatsLeft = r.capacity == null ? null : Math.max(0, r.capacity - r.activeRegistrations);
        return (
          <>
            {r.capacity == null
              ? `${formatInteger(r.activeRegistrations)} sold`
              : `${formatInteger(r.activeRegistrations)} / ${formatInteger(r.capacity)}`}
            {seatsLeft != null && <span className="slug">{formatInteger(seatsLeft)} left</span>}
          </>
        );
      },
    },
    {
      id: "paid",
      header: "Collected",
      sortKey: "paid",
      width: "9rem",
      nowrap: true,
      render: (r) => formatMoney(r.totalPaidCents),
    },
    {
      id: "balance",
      header: "Balance due",
      sortKey: "balance",
      width: "9rem",
      nowrap: true,
      render: (r) =>
        r.balanceDueCents > 0 ? (
          <>
            <strong>{formatMoney(r.balanceDueCents)}</strong>
            <span className="slug">{formatInteger(r.balanceDueRegistrations)} outstanding</span>
          </>
        ) : (
          <span style={{ color: `${colors.navy}66` }}>-</span>
        ),
    },
    {
      id: "purchases",
      header: "Purchases",
      sortKey: "registrations",
      width: "8rem",
      render: (r) => (
        <>
          <span>{formatInteger(r.registrations)}</span>
          {openRegs === r.id && <span className="slug">details open</span>}
          {r.cancelledRegistrations > 0 && (
            <span className="slug">{formatInteger(r.cancelledRegistrations)} cancelled</span>
          )}
        </>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      width: "4.5rem",
      align: "right",
      render: (r) => {
        const msg = followupMsg[r.id];
        return (
          <>
            <AdminRowActions
              label={`${r.title} actions`}
              actions={[
                ...(r.registrations > 0
                  ? [
                      {
                        label: openRegs === r.id ? "Hide purchases" : "View purchases",
                        onSelect: () => viewRegistrations(r.id),
                      },
                    ]
                  : []),
                {
                  label: "Purchases page",
                  to: `/admin/registrations?retreatId=${r.id}`,
                },
                ...(r.balanceDueRegistrations > 0
                  ? [
                      {
                        label: "Email balances",
                        onSelect: () => setFollowupRetreat(r),
                      },
                    ]
                  : []),
                {
                  label: "Edit",
                  onSelect: () => startEdit(r),
                },
              ]}
            />
            {msg && <span className={msg.ok ? "row-msg ok" : "row-msg err"}>{msg.text}</span>}
          </>
        );
      },
    },
  ];

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Retreats</h1>
      </div>

      {editing && form ? (
        <form onSubmit={submit} className="paper-card">
          <AdminFormHeader
            title={editing.title}
            eyebrow="Edit retreat"
            backLabel="Back to retreats"
            onBack={cancel}
          />

          <div className="grid2">
            <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required />
            <Field label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
          </div>

          <div className="grid2">
            <label className="fld">
              <span>Status</span>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as FormState["status"] })}
              >
                <option value="draft">draft</option>
                <option value="open">open</option>
                <option value="sold-out">sold-out</option>
                <option value="past">past</option>
              </select>
            </label>
            <Field
              label="Capacity (blank = unlimited)"
              value={form.capacity}
              onChange={(v) => setForm({ ...form, capacity: v })}
            />
          </div>

          <Field
            label="Klaviyo attendee list ID"
            value={form.klaviyoListId}
            onChange={(v) => setForm({ ...form, klaviyoListId: v })}
          />

          {/* Price Tiers */}
          <div className="tiers-section">
            <div className="tiers-header">
              <span className="tiers-label">Price Tiers</span>
              <button type="button" className="btn-ghost sm" onClick={addTier}>
                + Add tier
              </button>
            </div>
            {form.tiers.length === 0 && (
              <p className="note">No tiers configured. Click "+ Add tier" to add one.</p>
            )}
            {form.tiers.map((tier, i) => (
              <div key={i} className="tier-row paper-card-inner">
                <div className="grid2">
                  <Field
                    label="Tier name"
                    value={tier.name}
                    onChange={(v) => setTier(i, { name: v })}
                    required
                  />
                  <label className="fld">
                    <span>Tier type</span>
                    <select
                      value={tier.tierType}
                      onChange={(e) => setTier(i, { tierType: e.target.value as TierForm["tierType"] })}
                    >
                      <option value="full">full</option>
                      <option value="deposit">deposit</option>
                      <option value="addon">addon</option>
                    </select>
                  </label>
                </div>
                <div className="grid2">
                  <Field
                    label="Price (USD)"
                    value={tier.priceDollars}
                    onChange={(v) => setTier(i, { priceDollars: v })}
                    required
                  />
                  <Field
                    label="Balance due (USD, optional)"
                    value={tier.balanceDueDollars}
                    onChange={(v) => setTier(i, { balanceDueDollars: v })}
                  />
                </div>
                <Field
                  label="Description (optional)"
                  value={tier.description}
                  onChange={(v) => setTier(i, { description: v })}
                />
                <div style={{ textAlign: "right" }}>
                  <button type="button" className="btn-danger sm" onClick={() => removeTier(i)}>
                    Remove tier
                  </button>
                </div>
              </div>
            ))}
          </div>

          {err && <p className="err">{err}</p>}
          <div className="actions">
            <button type="submit" className="btn-woven" disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" className="btn-ghost" onClick={cancel}>
              Cancel
            </button>
          </div>

          {/* Klaviyo sync */}
          <div className="sync-section">
            <span className="tiers-label">Klaviyo attendee sync</span>
            <p className="note">
              Subscribes every non-cancelled registration for this retreat to the Klaviyo list above.
              Set the list ID and Save first. Safe to re-run (Klaviyo upserts).
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn-ghost"
                disabled={syncBusy || !form.klaviyoListId}
                onClick={syncKlaviyo}
              >
                {syncBusy ? "Syncing…" : "Sync attendees to Klaviyo"}
              </button>
            </div>
            {syncMsg && <p className={syncMsg.toLowerCase().includes("failed") ? "err" : "note sync-ok"}>{syncMsg}</p>}
          </div>
        </form>
      ) : retreatsQuery.isLoading ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search retreats, slugs, status, list IDs..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "open", label: "Open" },
                { value: "all", label: "All statuses" },
                { value: "draft", label: "Draft" },
                { value: "sold-out", label: "Sold out" },
                { value: "past", label: "Past" },
              ]}
            />
            <AdminTableFilterSelect
              label="Balance"
              value={balanceFilter}
              onChange={setBalanceFilter}
              options={[
                { value: "all", label: "All balances" },
                { value: "outstanding", label: "Outstanding" },
                { value: "paid", label: "Paid up" },
                { value: "empty", label: "No purchases" },
              ]}
            />
          </AdminTableControls>

          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No retreats yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No retreats match those filters.</p>
          ) : (
            <AdminDataTable
              columns={columns}
              rows={tableRows}
              getRowKey={(r) => r.id}
              sort={sort}
              onSort={requestSort}
              minWidth={1120}
              renderExpandedRow={(r) =>
                openRegs === r.id ? (
                  <tr className="regs-row">
                    <td colSpan={columns.length}>
                      {regsLoading ? (
                        <p className="regs-empty">Loading purchases...</p>
                      ) : regs.length === 0 ? (
                        <p className="regs-empty">No purchases yet.</p>
                      ) : (
                        <div className="regs-wrap">
                          {regsSummary && (
                            <p className="regs-summary">
                              {formatInteger(regsSummary.count)} purchase
                              {regsSummary.count === 1 ? "" : "s"} ·{" "}
                              {formatMoney(regsSummary.totalPaidCents)} collected
                              {regsSummary.balanceDueCents > 0 &&
                                ` · ${formatMoney(regsSummary.balanceDueCents)} balance due`}
                            </p>
                          )}
                          <table className="regs-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Tier</th>
                                <th>Paid</th>
                                <th>Balance</th>
                                <th>Status</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {regs.map((reg) => (
                                <tr key={reg.id}>
                                  <td>{reg.name ?? "-"}</td>
                                  <td>{reg.email}</td>
                                  <td>{reg.tierName ?? "-"}</td>
                                  <td>{formatMoney(reg.amountPaidCents)}</td>
                                  <td>{reg.balanceDueCents > 0 ? formatMoney(reg.balanceDueCents) : "-"}</td>
                                  <td>{reg.status}</td>
                                  <td>{formatDate(reg.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : null
              }
            />
          )}
        </>
      )}
      <ConfirmModal
        open={Boolean(followupRetreat)}
        title="Email balance reminders?"
        description={
          followupRetreat
            ? `This will create fresh Stripe balance links and email ${formatInteger(
                followupRetreat.balanceDueRegistrations,
              )} outstanding attendee${followupRetreat.balanceDueRegistrations === 1 ? "" : "s"} for ${
                followupRetreat.title
              }.`
            : undefined
        }
        confirmLabel="Email balances"
        busy={followupBusy}
        onConfirm={sendBalanceFollowups}
        onClose={() => setFollowupRetreat(null)}
      />
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </label>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px; box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    display: inline-block; text-decoration: none;
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-ghost:disabled { opacity: 0.5; cursor: default; }
  .btn-danger { background: ${colors.cream}; border: 1px solid ${colors.berryRose}; color: ${colors.berryRose};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border-radius: 7px; padding: 9px 16px; cursor: pointer; }
  .btn-danger:hover { background: ${colors.berryRose}; color: #fff; }
  .sm { padding: 6px 12px; margin-left: 6px; font-size: 0.62rem; }

  .admin-data-table .title-cell strong { display: block; white-space: normal; line-height: 1.35; }
  .admin-data-table .slug { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }
  .btn-link { background: none; border: none; padding: 0; cursor: pointer; font-family: ${fonts.body}; font-size: 13.5px; font-weight: 700; color: ${colors.berryRose}; }
  .btn-link:hover { text-decoration: underline; }
  .row-msg { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; margin-top: 0.35rem; white-space: normal; }
  .row-msg.ok { color: #2d7a52; }
  .row-msg.err { color: ${colors.berryRose}; }
  .regs-row td { background: ${colors.blushLight}; padding: 0.9rem 1rem; }
  .regs-empty { font-family: ${fonts.body}; font-size: 13px; color: rgba(8,45,61,0.55); margin: 0; }
  .regs-summary { font-family: ${fonts.body}; font-size: 12px; font-weight: 700; color: ${colors.navy}; margin: 0 0 0.6rem; }
  .regs-wrap { overflow-x: auto; }
  .regs-table { width: 100%; min-width: 780px; border-collapse: collapse; background: #fff; border-radius: 10px; overflow: hidden; }
  .regs-table th { text-align: left; padding: 0.45rem 0.7rem; font-family: ${fonts.body}; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; color: rgba(8,45,61,0.6); background: #fff; border-bottom: 1px solid rgba(201,147,76,0.2); }
  .regs-table td { padding: 0.45rem 0.7rem; font-family: ${fonts.body}; font-size: 12.5px; color: rgba(8,45,61,0.85); border-bottom: 1px solid rgba(201,147,76,0.1); }
  .regs-table tbody tr:last-child td { border-bottom: none; }

  .paper-card { background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; padding: 1.5rem 1.75rem; }
  .paper-card-inner { background: ${colors.blushLight}; border: 1px dashed rgba(201,147,76,0.30); border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 0.75rem; }
  .form-legend { font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem; color: ${colors.navy}; display: block; margin-bottom: 1.25rem; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span { display: block; font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); margin-bottom: 0.35rem; }
  .fld input[type=text], .fld select {
    width: 100%; padding: 0.55rem 0.9rem; border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy}; font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
  }
  .fld input:focus, .fld select:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; }
  .err { color: ${colors.berryRose}; font-family: ${fonts.body}; font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0; }
  .note { font-family: ${fonts.body}; font-size: 0.75rem; color: rgba(8,45,61,0.5); margin-top: 0.75rem; }
  .sync-ok { color: #2a7c55; }

  .tiers-section { margin: 1.25rem 0; }
  .tiers-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
  .tiers-label { font-family: ${fonts.body}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65); }

  .sync-section { margin-top: 1.75rem; padding-top: 1.25rem; border-top: 1px dashed rgba(201,147,76,0.30); }
`;
